import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { App as AppPlugin } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

import {
  DEFAULT_SCREEN0_LANGUAGE,
  type Screen0LanguageCode,
  getScreen0LanguageLabel,
  isSupportedScreen0Language,
  resolveScreen0LanguageCode,
} from "./language-registry";
import {
  NativeSettings,
  isNativeSettingsAvailable,
} from "@/lib/native/native-settings";

type Screen0LanguageSnapshot = {
  languageCode: Screen0LanguageCode;
  languageLabel: string;
  systemLanguageTag: string | null;
  isUnsupportedSystemLanguage: boolean;
  /** Android 13+ per-app locale; false on older Android / web; unused on iOS UI. */
  supportsPerAppLocale: boolean;
};

type Screen0LanguageContextValue = Screen0LanguageSnapshot & {
  refreshLanguage: () => Promise<Screen0LanguageSnapshot>;
};

const Screen0LanguageContext =
  createContext<Screen0LanguageContextValue | null>(null);

const DEFAULT_LANGUAGE_SNAPSHOT: Screen0LanguageSnapshot = {
  languageCode: DEFAULT_SCREEN0_LANGUAGE,
  languageLabel: getScreen0LanguageLabel(DEFAULT_SCREEN0_LANGUAGE),
  systemLanguageTag: null,
  isUnsupportedSystemLanguage: false,
  supportsPerAppLocale: false,
};

async function resolveNativeLanguagePreference(): Promise<{
  languageTag: string | null;
  supportsPerAppLocale: boolean;
}> {
  if (isNativeSettingsAvailable()) {
    try {
      const result = await NativeSettings.getPreferredLanguage();
      return {
        languageTag: result.languageTag || null,
        supportsPerAppLocale: result.supportsPerAppLocale === true,
      };
    } catch (error) {
      console.error(
        "Unable to resolve preferred language from native settings",
        error,
      );
    }
  }

  if (typeof navigator !== "undefined") {
    return {
      languageTag: navigator.languages?.[0] ?? navigator.language ?? null,
      supportsPerAppLocale: false,
    };
  }

  return { languageTag: null, supportsPerAppLocale: false };
}

async function resolveLanguageSnapshot(): Promise<Screen0LanguageSnapshot> {
  const { languageTag: systemLanguageTag, supportsPerAppLocale } =
    await resolveNativeLanguagePreference();
  const languageCode = resolveScreen0LanguageCode(systemLanguageTag);

  return {
    languageCode,
    languageLabel: getScreen0LanguageLabel(languageCode),
    systemLanguageTag,
    isUnsupportedSystemLanguage:
      Boolean(systemLanguageTag) &&
      !isSupportedScreen0Language(systemLanguageTag),
    supportsPerAppLocale,
  };
}

export function Screen0LanguageProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [languageSnapshot, setLanguageSnapshot] =
    useState<Screen0LanguageSnapshot>(DEFAULT_LANGUAGE_SNAPSHOT);

  const refreshLanguage = useMemo(
    () => async () => {
      const nextSnapshot = await resolveLanguageSnapshot();
      setLanguageSnapshot(nextSnapshot);
      return nextSnapshot;
    },
    [],
  );

  useEffect(() => {
    let isMounted = true;

    const syncLanguage = async () => {
      const nextSnapshot = await resolveLanguageSnapshot();

      if (isMounted) {
        setLanguageSnapshot(nextSnapshot);
      }
    };

    void syncLanguage();

    if (!Capacitor.isNativePlatform()) {
      return () => {
        isMounted = false;
      };
    }

    const listener = AppPlugin.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        void syncLanguage();
      }
    });

    return () => {
      isMounted = false;
      listener.then((handle) => handle.remove());
    };
  }, []);

  useLayoutEffect(() => {
    document.documentElement.lang = languageSnapshot.languageCode;
  }, [languageSnapshot.languageCode]);

  const value = useMemo<Screen0LanguageContextValue>(
    () => ({
      ...languageSnapshot,
      refreshLanguage,
    }),
    [languageSnapshot, refreshLanguage],
  );

  return (
    <Screen0LanguageContext.Provider value={value}>
      {children}
    </Screen0LanguageContext.Provider>
  );
}

export function useScreen0Language() {
  const context = useContext(Screen0LanguageContext);

  if (!context) {
    throw new Error(
      "useScreen0Language must be used inside Screen0LanguageProvider.",
    );
  }

  return context;
}
